import { useState, type FormEvent } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import {
  getUsers,
  getClientOptions,
  createUser,
  updateUser,
  deleteUser,
  resendInvite,
  ApiError,
  type UserRole,
} from '../lib/api';

const ROLE_LABEL: Record<UserRole, string> = {
  super_admin: 'Super admin',
  accounting: 'Accounting',
  clinic_view: 'Clinic view',
};

/** Strong, readable password (no ambiguous chars), generated in the browser. */
function generatePassword(len = 20): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join('');
}

export const UsersPage = () => {
  const { user: me, can } = useAuth();
  const usersState = useFetch(getUsers);
  const clientsState = useFetch(getClientOptions);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const manage = can('users:manage');
  const canSuper = can('users:manage_super');
  const assignableRoles: UserRole[] = canSuper
    ? ['super_admin', 'accounting', 'clinic_view']
    : ['accounting', 'clinic_view'];

  const clients = clientsState.data?.clients ?? [];
  const clientName = (id: string | null) =>
    id ? (clients.find((c) => c.id === id)?.name ?? id) : '—';

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      usersState.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed');
    }
  }

  return (
    <Page>
      <PageHeader
        title="Users"
        sub={usersState.data ? `${usersState.data.users.length} users` : undefined}
        actions={
          manage ? (
            <button className="btn btn--primary" onClick={() => setAdding((a) => !a)}>
              {adding ? 'Cancel' : 'Add user'}
            </button>
          ) : undefined
        }
      />

      {error && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(239,68,68,.08)',
            color: 'var(--status-error-ink, #b91c1c)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {notice && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(16,185,129,.08)', color: 'var(--status-success-ink,#047857)', fontSize: 13, wordBreak: 'break-all' }}>
          {notice}
        </div>
      )}

      {adding && manage && (
        <CreateUserForm
          assignableRoles={assignableRoles}
          clients={clients.map((c) => ({ id: c.id, name: c.name }))}
          onCancel={() => setAdding(false)}
          onCreate={async (body) => {
            setError(null);
            setNotice(null);
            try {
              const r = await createUser(body);
              usersState.reload();
              setAdding(false);
              if (r.invited) {
                setNotice(
                  r.emailSent
                    ? `Invite emailed to ${r.user.email}.`
                    : `User created. Email isn't configured — send them this link: ${r.inviteUrl}`,
                );
              }
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Could not create user');
            }
          }}
        />
      )}

      <Async state={usersState} isEmpty={(d) => d.users.length === 0} emptyLabel="No users yet.">
        {({ users }) => (
          <PCard>
            <table className="p-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Clinic</th>
                  <th>Status</th>
                  <th>Last login</th>
                  {manage && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === me?.id;
                  // Can the current actor act on this row? (super_admins need manage_super.)
                  const actable = manage && (u.role !== 'super_admin' || canSuper);
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{u.name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{u.email}</div>
                      </td>
                      <td>
                        {actable && !isSelf ? (
                          <select
                            className="select"
                            value={u.role}
                            onChange={(e) => run(() => updateUser(u.id, { role: e.target.value as UserRole }))}
                          >
                            {assignableRoles.map((r) => (
                              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                            ))}
                            {/* keep showing current role even if actor can't assign it */}
                            {!assignableRoles.includes(u.role) && (
                              <option value={u.role}>{ROLE_LABEL[u.role]}</option>
                            )}
                          </select>
                        ) : (
                          ROLE_LABEL[u.role]
                        )}
                      </td>
                      <td className="muted">{u.role === 'clinic_view' ? clientName(u.clientId) : '—'}</td>
                      <td>
                        <StatusPill tone={u.status === 'active' ? 'success' : 'neutral'}>
                          {u.status === 'active' ? 'Active' : 'Disabled'}
                        </StatusPill>
                      </td>
                      <td className="muted tabular">
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'never'}
                      </td>
                      {manage && (
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {actable && !isSelf ? (
                            <div style={{ display: 'inline-flex', gap: 8 }}>
                              <button
                                className="btn"
                                onClick={() =>
                                  run(() =>
                                    updateUser(u.id, { status: u.status === 'active' ? 'disabled' : 'active' }),
                                  )
                                }
                              >
                                {u.status === 'active' ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                className="btn"
                                onClick={async () => {
                                  setError(null);
                                  setNotice(null);
                                  try {
                                    const r = await resendInvite(u.id);
                                    setNotice(
                                      r.emailSent
                                        ? `Set-password link emailed to ${u.email}.`
                                        : `Email isn't configured — send them this link: ${r.inviteUrl}`,
                                    );
                                  } catch (err) {
                                    setError(err instanceof ApiError ? err.message : 'Could not send link');
                                  }
                                }}
                              >
                                Send link
                              </button>
                              <button
                                className="btn"
                                onClick={() => {
                                  if (window.confirm(`Delete ${u.email}? This cannot be undone.`))
                                    run(() => deleteUser(u.id));
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span className="muted" style={{ fontSize: 12 }}>{isSelf ? 'you' : '—'}</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </PCard>
        )}
      </Async>
    </Page>
  );
};

function CreateUserForm({
  assignableRoles,
  clients,
  onCreate,
  onCancel,
}: {
  assignableRoles: UserRole[];
  clients: { id: string; name: string }[];
  onCreate: (body: {
    email: string;
    name: string;
    password?: string;
    role: UserRole;
    clientId?: string;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>(assignableRoles[0]);
  const [clientId, setClientId] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onCreate({
        email,
        name,
        password: invite ? undefined : password,
        role,
        clientId: role === 'clinic_view' ? clientId : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PCard style={{ padding: 16 }}>
      <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <Field label="Name">
          <input className="select" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Email">
          <input className="select" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        {!invite && (
          <Field label="Password">
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="select"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                style={{ flex: 1, minWidth: 150 }}
              />
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setPassword(generatePassword());
                  setShowPw(true);
                }}
              >
                Generate
              </button>
              <button type="button" className="btn" onClick={() => setShowPw((s) => !s)}>
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </Field>
        )}
        <Field label="Role">
          <select className="select" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            {assignableRoles.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
            ))}
          </select>
        </Field>
        {role === 'clinic_view' && (
          <Field label="Clinic">
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
              <option value="">Select clinic…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Setup">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, height: 36 }}>
            <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
            Email an invite (they set their own password)
          </label>
        </Field>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? (invite ? 'Inviting…' : 'Creating…') : invite ? 'Send invite' : 'Create user'}
          </button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </PCard>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--p-muted-fg)' }}>
    {label}
    {children}
  </label>
);
